import React, { ReactNode, useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useAuth } from '@/lib/authContext';
import { UserRole } from '@/types';
import AuthGuard from './guards/AuthGuard';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { supabase } from '@/lib/supabase';

interface LayoutProps {
  children: ReactNode;
  rol?: UserRole;
}

// Material Symbols icon names mapped to menu items
const menuSections = {
  admin: [
    {
      label: 'Principal',
      items: [
        { nombre: 'Dashboard', href: '/admin', icon: 'dashboard' },
        { nombre: 'Usuarios y Roles', href: '/admin/usuarios', icon: 'group' },
        { nombre: 'Clientes y Asesores', href: '/admin/clientes-asesores', icon: 'handshake' },
      ],
    },
    {
      label: 'Gestión',
      items: [
        { nombre: 'Documentos', href: '/admin/documentos', icon: 'folder_open' },
        { nombre: 'Sync Cegid', href: '/admin/sync-cegid', icon: 'sync' },
        { nombre: 'Contabilizador IA', href: '/admin/contabilizador-ia', icon: 'smart_toy' },
        { nombre: 'Panel Financiero', href: '/admin/panel-financiero-completo', icon: 'bar_chart' },
        { nombre: 'Asistente IA', href: '/admin/asistente', icon: 'psychology' },
        { nombre: 'Configuración', href: '/admin/configuracion', icon: 'settings' },
      ],
    },
  ],
  asesor: [
    {
      label: 'Principal',
      items: [
        { nombre: 'Inicio', href: '/asesor', icon: 'dashboard' },
        { nombre: 'Documentos', href: '/asesor/documentos-pendientes', icon: 'folder_open' },
      ],
    },
    {
      label: 'Gestión',
      items: [
        { nombre: 'Clientes', href: '/asesor/clientes', icon: 'group' },
        { nombre: 'Facturación', href: '/asesor/facturacion', icon: 'receipt' },
        { nombre: 'Incidencias', href: '/asesor/incidencias', icon: 'error' },
      ],
    },
    {
      label: 'Herramientas',
      items: [
        { nombre: 'Cumplimiento Fiscal', href: '/asesor/cumplimiento-fiscal', icon: 'balance' },
        { nombre: 'Financiero', href: '/asesor/panel-financiero', icon: 'trending_up' },

        { nombre: 'Asistente IA', href: '/asesor/asistente', icon: 'psychology' },
        { nombre: 'Papelera', href: '/asesor/papelera', icon: 'delete' },
        { nombre: 'Configuración', href: '/asesor/configuracion', icon: 'settings' },
      ],
    },
  ],
  cliente: [
    {
      label: 'Principal',
      items: [
        { nombre: 'Dashboard', href: '/cliente', icon: 'dashboard' },
        { nombre: 'Documentos', href: '/cliente/documentos', icon: 'folder_open' },
        { nombre: 'Financiero', href: '/cliente/panel-financiero', icon: 'trending_up' },
      ],
    },
    {
      label: 'Gestión',
      items: [
        { nombre: 'Incidencias', href: '/cliente/incidencias', icon: 'chat' },
        { nombre: 'Mi Perfil', href: '/cliente/perfil', icon: 'person' },
        { nombre: 'Configuración', href: '/cliente/configuracion', icon: 'settings' },
      ],
    },
  ],
};

const roleLabels: Record<UserRole, string> = {
  admin: 'Administrador',
  asesor: 'Asesor',
  cliente: 'Cliente',
};

export default function Layout({ children, rol }: LayoutProps) {
  const { user, logout } = useAuth();
  const router = useRouter();
  const currentRole = rol || user?.rol;
  const sections = currentRole ? menuSections[currentRole] : [];
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [pendingAlerts, setPendingAlerts] = useState(0);

  useEffect(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    if (saved === 'true') setSidebarCollapsed(true);
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('alert_history')
      .select('id', { count: 'exact', head: true })
      .eq('delivery_status', 'pending')
      .then(({ count }) => setPendingAlerts(count ?? 0));
  }, [user?.id]);

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebar-collapsed', String(next));
      return next;
    });
  };

  // Flatten for breadcrumb lookup
  const allItems = sections.flatMap(s => s.items);

  const isActive = (href: string) => {
    if (href === `/${currentRole}`) return router.pathname === href;
    // Documentos nav item should be active on both document pages
    if (href === '/asesor/documentos-pendientes') {
      return router.pathname === '/asesor/documentos-pendientes' ||
             router.pathname === '/asesor/documentos-contabilizados';
    }
    return router.pathname.startsWith(href);
  };

  const currentPageName = allItems.find(item => isActive(item.href))?.nombre || 'Inicio';

  const handleLogout = async () => {
    try {
      await logout();
    } catch (e) {
      console.error(e);
    }
  };

  const userInitials = user?.nombre
    ? user.nombre.split(' ').map((n: string) => n.charAt(0)).join('').substring(0, 2).toUpperCase()
    : 'U';

  const SidebarContent = ({ onLinkClick, collapsed = false, showToggle = false, onToggle }: { onLinkClick?: () => void; collapsed?: boolean; showToggle?: boolean; onToggle?: () => void }) => (
    <>
      {/* Logo */}
      <div className={cn("h-24 flex items-center", collapsed ? "justify-center px-0" : "px-8")}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-slate-50 border border-calm-border flex items-center justify-center flex-shrink-0">
            <Image src="/logo.png" alt="E2" width={28} height={28} className="w-7 h-7" />
          </div>
          {!collapsed && (
            <div className="flex flex-col">
              <span className="font-display font-medium text-base tracking-wide text-calm-text uppercase">Externaliza2</span>
              <span className="text-[10px] text-calm-muted tracking-widest uppercase font-light">
                {roleLabels[currentRole as UserRole] || 'Documentos'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className={cn("flex-1 overflow-y-auto space-y-1", collapsed ? "px-3" : "px-6")}>
        {sections.map((section, sIdx) => (
          <div key={section.label} className={sIdx > 0 ? 'pt-4 pb-2' : ''}>
            {!collapsed && (
              <p className="px-4 text-[10px] font-medium text-calm-muted uppercase tracking-[0.2em] mb-2 mt-1">
                {section.label}
              </p>
            )}
            {section.items.map((item) => {
              const active = isActive(item.href);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={onLinkClick}
                  title={collapsed ? item.nombre : undefined}
                  className={cn(
                    'flex items-center rounded-2xl text-sm transition-all duration-200',
                    collapsed ? 'justify-center px-0 py-2' : 'gap-4 px-4 py-2',
                    active
                      ? 'bg-gradient-to-r from-fresh-sky-100 to-fresh-sky-50 text-fresh-sky-700 font-medium border-l-2 border-fresh-sky-500'
                      : 'text-calm-muted hover:bg-fresh-sky-50 hover:text-fresh-sky-600 font-light'
                  )}
                >
                  <span className="material-symbols-outlined">{item.icon}</span>
                  {!collapsed && <span>{item.nombre}</span>}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Toggle button */}
      {showToggle && onToggle && (
        <div className={cn("px-6 py-2", collapsed && "px-3")}>
          <button
            onClick={onToggle}
            className="w-full flex items-center justify-center py-2 rounded-2xl text-calm-muted hover:text-calm-text hover:bg-slate-50 transition-all"
            title={collapsed ? "Expandir menú" : "Contraer menú"}
          >
            <span className="material-symbols-outlined text-lg">
              {collapsed ? 'chevron_right' : 'chevron_left'}
            </span>
          </button>
        </div>
      )}

      {/* User footer */}
      <div className={cn("px-6 py-4 border-t border-calm-border", collapsed && "px-3")}>
        <div className={cn("flex items-center gap-3", collapsed && "justify-center")}>
          <div className="w-8 h-8 rounded-full bg-white border border-calm-border flex items-center justify-center text-[10px] font-medium text-calm-muted flex-shrink-0">
            {userInitials}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-calm-text truncate">{user?.nombre || 'Usuario'}</div>
              <button onClick={handleLogout} className="text-[10px] text-calm-muted hover:text-calm-text transition-colors">
                Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  const layoutContent = (
    <div className="flex h-screen overflow-hidden">
      {/* ─── Sidebar (Desktop) ─── */}
      {user && sections.length > 0 && (
        <aside className={cn("hidden lg:flex lg:flex-col bg-calm-sidebar border-r border-calm-border h-full flex-shrink-0 z-30 transition-all duration-300", sidebarCollapsed ? "w-20" : "w-72")}>
          <SidebarContent collapsed={sidebarCollapsed} showToggle={true} onToggle={toggleSidebar} />
        </aside>
      )}

      {/* ─── Mobile Sidebar (Sheet) ─── */}
      {user && sections.length > 0 && (
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetContent
            side="left"
            className="w-72 p-0 bg-calm-sidebar border-r border-calm-border"
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Menú de navegación</SheetTitle>
            </SheetHeader>
            <SidebarContent onLinkClick={() => setSidebarOpen(false)} collapsed={false} showToggle={false} />
          </SheetContent>
        </Sheet>
      )}

      {/* ─── Main content area ─── */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Header */}
        <header className="h-24 bg-transparent flex items-center justify-between px-6 lg:px-12 flex-shrink-0">
          <div className="flex items-center gap-4">
            {/* Mobile menu button */}
            {user && sections.length > 0 && (
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden material-symbols-outlined text-calm-muted hover:text-calm-text transition-colors"
              >
                menu
              </button>
            )}
            <span className="text-calm-muted text-sm font-light uppercase tracking-widest">Dashboard</span>
            <span className="text-calm-border">/</span>
            <span className="text-calm-text text-sm font-light uppercase tracking-widest">{currentPageName}</span>
          </div>

          {/* Notification bell */}
          {user && (
            <div className="flex items-center gap-3">
              <Link href={currentRole === 'admin' ? '/admin' : currentRole === 'asesor' ? '/asesor' : '/cliente'} className="relative p-2 rounded-2xl hover:bg-fresh-sky-50 transition-colors group">
                <span className="material-symbols-outlined text-calm-muted group-hover:text-fresh-sky-600 transition-colors">notifications</span>
                {pendingAlerts > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
                    {pendingAlerts > 9 ? '9+' : pendingAlerts}
                  </span>
                )}
              </Link>
            </div>
          )}
        </header>

        {/* Page content */}
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </main>
    </div>
  );

  if (rol) {
    return (
      <AuthGuard allowedRoles={[rol]}>
        {layoutContent}
      </AuthGuard>
    );
  }

  return layoutContent;
}
